import {
  looksNumericForChart,
  looksTemporalForChart,
} from "./chart-field-guess";
import {
  Field,
  fieldLabel,
  isBooleanField,
  isNumeric,
  isPrimaryKey,
  isStructured,
  isTemporal,
} from "./schema-field";

export type ChartForm = "bar" | "line" | "pie";

export interface ChartPoint {
  label: string;
  value: number;
}

export interface ChartSpec {
  form: ChartForm;
  points: ChartPoint[];
  /** Sentence describing what was actually measured, shown under the title. */
  caption: string;
  /** Set when the aggregate covers only the rows that were fetched. */
  partial: boolean;
}

// Beyond this a bar chart stops being readable, so the tail is folded into a
// single "Other" bar rather than dropped, which would misstate the total.
const MAX_CATEGORIES = 10;

// A field with nearly as many distinct values as there are rows is an id or a
// free-text column, not something worth grouping by.
const MAX_CATEGORY_RATIO = 0.6;

/** Keep in sync with chart-field-guess: skip category compounds like price_tier. */
const MEASURE_HINTS =
  /(amount|total|sum|budget|cost|price|value|revenue|spend|score|qty|quantity|count|hours|duration)(?![\w-]*(?:tier|level|grade|type|kind|category|class|status)\b)/i;

const toNumber = (value: unknown): number | null => {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  // Postgres hands back numeric as a string, so this is the common path.
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

/**
 * Caption for a summed measure. "Total Budget" reads well; "Total Total" does
 * not when the column is itself named Total.
 */
export const measureCaption = (label: string): string => {
  const trimmed = label.trim();
  if (/^(total|sum|amount)$/i.test(trimmed)) return `Sum of ${trimmed}`;
  return `Total ${trimmed}`;
};

const categoryLabel = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

/**
 * Label a point that came back from SQL rather than from a JS row.
 *
 * `aggregateSeries` groups on `<column>::text`, so a boolean column arrives as
 * the database's own spelling of true. Postgres sends `true`/`false`, other
 * drivers send `t`/`f`, and printing either one under a caption that reads
 * "Is Active" mixes database English into product English. The client-side
 * aggregation this replaced ran every value through `categoryLabel`, which
 * mapped real booleans to Yes/No; that translation has to happen here now.
 *
 * Only applied when the schema says the grouped column is a boolean, so a text
 * column that genuinely stores the word "true" is left alone.
 */
export const seriesLabel = (raw: string, groupField?: Field): string => {
  if (raw === "" || raw === null || raw === undefined) return "\u2014";
  if (!isBooleanField(groupField)) return raw;

  const normalised = raw.trim().toLowerCase();
  if (["true", "t", "1", "yes"].includes(normalised)) return "Yes";
  if (["false", "f", "0", "no"].includes(normalised)) return "No";
  return raw;
};

const MONTH_BUCKET = /^(\d{4})-(\d{2})$/;

/**
 * Turn a server month bucket (`YYYY-MM`) into a locale month label.
 *
 * Points stay in the order the API returned (chronological by `YYYY-MM`);
 * only the painted text changes, so "Oct 2026" still sorts after "Sep 2026".
 */
export const monthBucketLabel = (
  raw: string,
  locale?: string,
): string => {
  const match = MONTH_BUCKET.exec(String(raw).trim());
  if (!match) return raw;
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (!Number.isInteger(year) || month < 1 || month > 12) return raw;
  // UTC noon avoids the label flipping a day when the host is west of UTC.
  const date = new Date(Date.UTC(year, month - 1, 1, 12));
  return new Intl.DateTimeFormat(locale, {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
};

const distinctCount = (rows: any[], name: string): number =>
  new Set(rows.map((row) => categoryLabel(row?.[name]))).size;

/**
 * Pick the column to group by. Enums first because the analyser only marks a
 * field as one when it saw a small fixed set, then booleans, then whichever
 * remaining string column has the fewest distinct values.
 */
const pickCategory = (rows: any[], schema: Field[]): Field | undefined => {
  const candidates = schema.filter(
    (field) =>
      !isPrimaryKey(field) &&
      !isTemporal(field) &&
      !isNumeric(field) &&
      rows.some((row) => row?.[field.name] !== undefined),
  );

  const usable = candidates.filter((field) => {
    const distinct = distinctCount(rows, field.name);
    return (
      distinct > 1 &&
      distinct <= Math.max(MAX_CATEGORIES * 2, 20) &&
      distinct <= Math.ceil(rows.length * MAX_CATEGORY_RATIO)
    );
  });

  const enums = usable.filter((field) => (field as any).isEnum);
  const booleans = usable.filter(isBooleanField);
  const pool = enums.length ? enums : booleans.length ? booleans : usable;

  return [...pool].sort(
    (a, b) => distinctCount(rows, a.name) - distinctCount(rows, b.name),
  )[0];
};

/** A numeric column worth summing. Keys and flags are not measures. */
const pickMeasure = (rows: any[], schema: Field[]): Field | undefined => {
  const numeric = schema.filter(
    (field) =>
      isNumeric(field) &&
      !isPrimaryKey(field) &&
      rows.some((row) => toNumber(row?.[field.name]) !== null),
  );
  return numeric.find((field) => MEASURE_HINTS.test(field.name)) ?? numeric[0];
};

const pickTemporal = (rows: any[], schema: Field[]): Field | undefined =>
  schema.find(
    (field) =>
      isTemporal(field) && rows.some((row) => row?.[field.name] != null),
  );

const monthKey = (value: unknown): string | null => {
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return null;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
};

const foldTail = (points: ChartPoint[]): ChartPoint[] => {
  if (points.length <= MAX_CATEGORIES) return points;
  const head = points.slice(0, MAX_CATEGORIES - 1);
  const tail = points.slice(MAX_CATEGORIES - 1);
  return [
    ...head,
    {
      label: `Other (${tail.length})`,
      value: tail.reduce((a, p) => a + p.value, 0),
    },
  ];
};

const aggregate = (
  rows: any[],
  keyOf: (row: any) => string | null,
  measure: Field | undefined,
): ChartPoint[] => {
  const totals = new Map<string, number>();
  rows.forEach((row) => {
    const key = keyOf(row);
    if (key === null) return;
    const amount = measure ? toNumber(row?.[measure.name]) : 1;
    if (amount === null) return;
    totals.set(key, (totals.get(key) ?? 0) + amount);
  });
  return Array.from(totals, ([label, value]) => ({ label, value }));
};

/**
 * Turn a collection's rows into something plottable.
 *
 * The API's ChartDto carries only a type and a collection name, never which
 * column is the axis and which is the measure, so the shape has to be inferred
 * from the data. Returns null when nothing sensible can be plotted, which the
 * caller renders as an explanation rather than an empty pair of axes.
 */
export const buildChartSpec = (
  requestedType: string | undefined,
  rows: any[],
  schema: Field[],
  fetched: number,
  total: number,
): ChartSpec | null => {
  if (!rows.length) return null;

  const wantsLine = /line|area|time|trend/i.test(requestedType ?? "");
  const measure = pickMeasure(rows, schema);
  const partial = total > fetched;
  const measured = measure
    ? measureCaption(fieldLabel(measure))
    : "Number of records";

  const temporal = wantsLine ? pickTemporal(rows, schema) : undefined;
  if (temporal) {
    const points = aggregate(
      rows,
      (row) => monthKey(row?.[temporal.name]),
      measure,
    ).sort((a, b) => a.label.localeCompare(b.label));
    if (points.length > 1) {
      return {
        form: "line",
        points,
        caption: `${measured} by month of ${fieldLabel(temporal)}`,
        partial,
      };
    }
  }

  const category = pickCategory(rows, schema);
  if (!category) return null;

  const points = foldTail(
    aggregate(rows, (row) => categoryLabel(row?.[category.name]), measure).sort(
      (a, b) => b.value - a.value,
    ),
  );
  if (!points.length) return null;

  return {
    form: "bar",
    points,
    caption: `${measured} by ${fieldLabel(category)}`,
    partial,
  };
};

/**
 * Which columns the server should group and sum for this chart.
 *
 * Picked from the schema alone, with no rows in hand, because the aggregate
 * runs in the database: the client no longer sees a sample it could inspect
 * first. Mirrors the row-based pickers below so a chart looks the same
 * whichever path produced it.
 */
export interface SeriesRequest {
  groupBy: string;
  measure?: string;
  bucket: "none" | "month";
  /** The chart type the API stored, so a PIE row does not render as bars. */
  type?: string;
}

/**
 * The axis the generator chose, when it recorded one.
 *
 * `seriesRequestFromSchema` guesses from the schema and returns the same first
 * match for every chart on a page, so three cards used to render one column
 * under three titles. A stored `groupBy` is the generator's actual decision and
 * always wins; the guess survives only for rows written before the column
 * existed.
 *
 * The measure is NOT guessed. A stored chart carries its own title, and
 * inventing a measure makes the plot contradict it: `Projects by status` drew a
 * budget-weighted pie reading 125.0k for two records, captioned "Total Budget
 * by Status". Omitting it leaves the aggregate on COUNT(*), which is what a
 * chart claiming "by status" means. The schema guess keeps its own measure
 * hunt, because a chart with no stored axis has no title to contradict.
 */
export const seriesRequestFromChart = (
  chart: {
    type?: string;
    groupBy?: string;
    bucket?: string;
    measure?: string;
  },
  schema: Field[]
): SeriesRequest | null => {
  if (!chart.groupBy) {
    return seriesRequestFromSchema(chart.type, schema);
  }

  // Only a measure the API actually stored, and only one this collection can
  // aggregate: an unknown column would make the server error rather than fall
  // back to a count, and measuring the group column double-counts the axis.
  // Sheets often types Total as STRING; name hints still let the SUM through.
  const stored = schema.find(
    (field) =>
      !!chart.measure &&
      field.name === chart.measure &&
      field.name !== chart.groupBy &&
      looksNumericForChart(field) &&
      !isPrimaryKey(field)
  );

  return {
    groupBy: chart.groupBy,
    measure: stored?.name,
    bucket: chart.bucket === "month" ? "month" : "none",
    type: chart.type,
  };
};

export const seriesRequestFromSchema = (
  requestedType: string | undefined,
  schema: Field[],
): SeriesRequest | null => {
  const usable = schema.filter((field) => !isPrimaryKey(field));

  const measure =
    usable.find(
      (field) => looksNumericForChart(field) && MEASURE_HINTS.test(field.name),
    ) ?? usable.find(looksNumericForChart);

  const wantsLine = /line|area|time|trend/i.test(requestedType ?? "");
  if (wantsLine) {
    const temporal = usable.find(looksTemporalForChart) ?? usable.find(isTemporal);
    if (temporal) {
      return {
        groupBy: temporal.name,
        measure: measure?.name,
        bucket: "month",
        type: requestedType,
      };
    }
  }

  const category =
    usable.find((field) => (field as any).isEnum) ??
    usable.find(isBooleanField) ??
    usable.find(
      (field) =>
        !looksNumericForChart(field) &&
        !looksTemporalForChart(field) &&
        !isNumeric(field) &&
        !isTemporal(field) &&
        !isStructured(field),
    );

  if (category) {
    return {
      groupBy: category.name,
      measure: measure?.name,
      bucket: "none",
      type: requestedType,
    };
  }

  const temporal = usable.find(looksTemporalForChart) ?? usable.find(isTemporal);
  if (temporal) {
    return {
      groupBy: temporal.name,
      measure: measure?.name,
      bucket: "month",
      type: requestedType,
    };
  }

  return null;
};

/**
 * How many month buckets still read as a short comparison, not a trend.
 * Below this, `seriesForm` draws bars; at or above it, a line.
 */
export const MONTH_LINE_MIN_POINTS = 4;

/**
 * The figure a persisted chart should draw.
 *
 * The API's requested type is authoritative for PIE vs BAR. Month buckets with
 * enough points stay a line; a two- or three-month series is a short comparison
 * and draws as bars instead of a nearly empty line.
 */
export const seriesForm = (
  request: SeriesRequest,
  points?: ChartPoint[],
): ChartForm => {
  if (request.bucket === "month") {
    if (points && points.length < MONTH_LINE_MIN_POINTS) return "bar";
    return "line";
  }
  return /pie/i.test(request.type ?? "") ? "pie" : "bar";
};


/**
 * Coerce aggregate point values to finite numbers.
 *
 * Sheets SUM can hand back digit strings. `Number.isFinite("12")` is false, so
 * a two-month series of string totals looked empty and the card vanished.
 */
export const coerceChartPoints = (
  points: Array<{ label: string; value: unknown }>,
): ChartPoint[] => {
  const out: ChartPoint[] = [];
  for (const point of points) {
    let value: number | null = null;
    if (typeof point.value === "number") {
      value = Number.isFinite(point.value) ? point.value : null;
    } else if (typeof point.value === "string" && point.value.trim()) {
      const parsed = Number(point.value);
      value = Number.isFinite(parsed) ? parsed : null;
    }
    if (value === null) continue;
    out.push({ label: String(point.label ?? ""), value });
  }
  return out;
};

/**
 * A series with nothing to show, so the card renders nothing at all.
 *
 * Only an empty (or all-null) series is refused. Equal categorical values are
 * valid. A one- or two-month series draws as bars via `seriesForm` — Sample
 * Orders has Sep + Oct, and a lone month bar is still a quantity.
 */
export const isUnplottableSeries = (
  _request: SeriesRequest,
  points: ChartPoint[]
): boolean => coerceChartPoints(points).length === 0;

export const specFromSeries = (
  series: {
    points: Array<{ label: string; value: unknown }>;
    groupBy: string;
    measure?: string;
    rows: number;
    truncated: boolean;
    filtered?: boolean;
  },
  request: SeriesRequest,
  schema: Field[],
): ChartSpec | null => {
  const points = coerceChartPoints(series.points);
  if (!points.length) return null;
  if (isUnplottableSeries(request, points)) return null;

  const labelOf = (name: string): string => {
    const field = schema.find((candidate) => candidate.name === name);
    return field ? fieldLabel(field) : name;
  };

  const measured = series.measure
    ? measureCaption(labelOf(series.measure))
    : "Number of records";
  const by =
    request.bucket === "month"
      ? `by month of ${labelOf(series.groupBy)}`
      : `by ${labelOf(series.groupBy)}`;
  const scope =
    series.filtered === true
      ? ` across ${series.rows.toLocaleString()} matching rows`
      : ` across all ${series.rows.toLocaleString()} rows`;
  const folded = series.truncated ? `, top ${points.length}` : "";

  const groupField = schema.find(
    (candidate) => candidate.name === series.groupBy,
  );

  return {
    form: seriesForm(request, points),
    // Month buckets arrive as YYYY-MM (sort-stable). Paint them as a locale
    // month without reordering. Categorical labels still go through
    // seriesLabel for boolean Yes/No etc.
    points:
      request.bucket === "month"
        ? points.map((point) => ({
            ...point,
            label: monthBucketLabel(point.label),
          }))
        : points.map((point) => ({
            ...point,
            label: seriesLabel(point.label, groupField),
          })),
    caption: `${measured} ${by}${scope}${folded}`,
    partial: false,
  };
};
